import React, { useEffect, useMemo, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import {
  localizeSurveyText,
  SurveyDetail,
  useSubmitSurvey,
  useSurvey,
} from '../../../helpers/api/surveys/surveys';
import { ErrorDisplay } from '../../ComponnentsUI/common/ErrorDisplay';
import { LoadingSpinner } from '../../ComponnentsUI/common/LoadingSpinner';

const SCALE = [1, 2, 3, 4, 5];

const SurveyDetailPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const { t, i18n } = useTranslation('Surveys');
  const navigate = useNavigate();
  const { data, isLoading, error } = useSurvey(surveyId);
  const submitMutation = useSubmitSurvey(surveyId || '');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [sectionComments, setSectionComments] = useState<Record<string, string>>({});

  const survey = data as SurveyDetail | null;
  useDocumentTitle(survey ? localizeSurveyText(survey.title, i18n.language) : t('documentTitle'));

  useEffect(() => {
    if (!survey?.current_response) {
      return;
    }
    setAnswers(survey.current_response.answers || {});
    setSectionComments(survey.current_response.section_comments || {});
  }, [survey?.current_response]);

  const questionIds = useMemo(() => {
    return survey?.sections.flatMap((section) => section.questions.map((question) => question.id)) || [];
  }, [survey]);

  const allAnswered = questionIds.every((questionId) => Number.isInteger(answers[questionId]));

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !survey) {
    return <ErrorDisplay message={t('loadError')} />;
  }

  const handleSubmit = async () => {
    if (!allAnswered || !surveyId) {
      return;
    }

    await submitMutation.mutateAsync({
      answers,
      sectionComments,
    });
    navigate('/surveys');
  };

  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <button
          type="button"
          onClick={() => navigate('/surveys')}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 transition hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <FiArrowLeft size={16} />
          {t('backToList')}
        </button>

        <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
            <span>{survey.is_due ? t('dueNow') : t('completed')}</span>
            <span className="text-gray-400 dark:text-gray-500">•</span>
            <span>{t('questionsCount', { count: questionIds.length })}</span>
          </div>
          <h1 className="mt-3 text-3xl font-bold text-gray-900 dark:text-white">
            {localizeSurveyText(survey.title, i18n.language)}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {localizeSurveyText(survey.description, i18n.language)}
          </p>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-blue-600 transition-all"
              style={{ width: `${questionIds.length === 0 ? 0 : (Object.keys(answers).length / questionIds.length) * 100}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('answeredCount', { count: Object.keys(answers).length, total: questionIds.length })}
          </p>
        </div>

        <div className="space-y-5">
          {survey.sections.map((section) => (
            <section
              key={section.id}
              className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700"
            >
              {(section.title || section.description) && (
                <div className="mb-5">
                  {section.title ? (
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {localizeSurveyText(section.title, i18n.language)}
                    </h2>
                  ) : null}
                  {section.description ? (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {localizeSurveyText(section.description, i18n.language)}
                    </p>
                  ) : null}
                </div>
              )}

              <div className="space-y-6">
                {section.questions.map((question) => (
                  <div key={question.id} className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {localizeSurveyText(question.prompt, i18n.language)}
                      </p>
                      {question.required ? (
                        <span className="shrink-0 text-xs font-semibold text-blue-600 dark:text-blue-300">
                          {t('required')}
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-2">
                      {SCALE.map((value) => {
                        const selected = answers[question.id] === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setAnswers((prev) => ({ ...prev, [question.id]: value }))}
                            className={`flex h-11 w-11 items-center justify-center rounded-full border text-sm font-semibold transition ${selected ? 'border-blue-600 bg-blue-600 text-white shadow-sm' : 'border-blue-100 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200'}`}
                          >
                            {value}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex justify-between text-[11px] text-gray-500 dark:text-gray-400">
                      <span>{t('stronglyDisagree')}</span>
                      <span>{t('stronglyAgree')}</span>
                    </div>
                  </div>
                ))}
              </div>

              {section.comment_prompt ? (
                <div className="mt-5">
                  <label className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
                    {localizeSurveyText(section.comment_prompt, i18n.language)}
                  </label>
                  <textarea
                    value={sectionComments[section.id] || ''}
                    onChange={(event) =>
                      setSectionComments((prev) => ({
                        ...prev,
                        [section.id]: event.target.value,
                      }))
                    }
                    placeholder={t('commentOptional')}
                    rows={4}
                    className="w-full rounded-2xl border border-gray-200 bg-blue-50/60 px-4 py-3 text-sm text-gray-800 outline-none transition focus:border-blue-400 focus:bg-white dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-100"
                  />
                </div>
              ) : null}
            </section>
          ))}
        </div>

        <div className="sticky bottom-4 flex justify-center">
          <button
            type="button"
            disabled={!allAnswered || submitMutation.isPending}
            onClick={handleSubmit}
            className="inline-flex min-w-[220px] items-center justify-center rounded-full bg-blue-600 px-8 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            {submitMutation.isPending ? t('submitting') : survey.current_response ? t('updateSubmission') : t('submit')}
          </button>
        </div>

        {!allAnswered ? (
          <p className="text-center text-sm text-amber-700 dark:text-amber-300">{t('answerAllQuestions')}</p>
        ) : null}
      </div>
    </div>
  );
};

export default SurveyDetailPage;
